// =====================================================================
// pendingMedia — frontend helpers for the strip-and-checklist tables
// =====================================================================
// Reads/writes `content_publication_pending_media`. Rows are seeded by
// the dispatch edge functions after Kit/Bento (no-media-endpoint)
// publishes; the checklist UI uses these helpers to surface pending
// images and to mark them resolved once the user has manually
// re-uploaded them.
// =====================================================================

/* eslint-disable @typescript-eslint/no-explicit-any -- Supabase boundary; see mem://preferences/lint-debt.md */
import { supabase } from "@/integrations/supabase/client";

/** Historically included `"micropost"`; retained as a single-member union
 *  so callers still pass a scope explicitly. */
export type PendingMediaScope = "publication";

export type PendingMediaRow = {
  id: string;
  workspace_id: string;
  publication_id?: string | null;
  dispatch_id: string | null;
  destination_id: string | null;
  bucket: string;
  storage_path: string;
  filename: string;
  alt_text: string | null;
  original_url: string | null;
  signed_url: string | null;
  signed_url_expires_at: string | null;
  uploaded_at: string | null;
  uploaded_by_user_id: string | null;
  created_at: string;
  updated_at: string;
};

const tableFor = (_scope: PendingMediaScope) => "content_publication_pending_media";

const parentColumnFor = (_scope: PendingMediaScope) => "publication_id";

export const listPendingMedia = async (
  scope: PendingMediaScope,
  workspaceId: string,
  parentId: string,
): Promise<PendingMediaRow[]> => {
  const { data, error } = await (supabase as any)
    .from(tableFor(scope))
    .select("*")
    .eq("workspace_id", workspaceId)
    .eq(parentColumnFor(scope), parentId)
    .order("created_at", { ascending: false });
  if (error) throw error;
  return (data ?? []) as PendingMediaRow[];
};

/** Refresh a signed URL when the cached one is past its 1h TTL. */
export const refreshSignedUrl = async (
  scope: PendingMediaScope,
  row: PendingMediaRow,
): Promise<PendingMediaRow> => {
  const { data, error } = await supabase.storage
    .from(row.bucket)
    .createSignedUrl(row.storage_path, 60 * 60);
  if (error || !data?.signedUrl) throw error ?? new Error("Failed to sign URL");
  const expires = new Date(Date.now() + 60 * 60 * 1000).toISOString();
  const { error: updErr } = await (supabase as any)
    .from(tableFor(scope))
    .update({ signed_url: data.signedUrl, signed_url_expires_at: expires })
    .eq("id", row.id);
  if (updErr) throw updErr;
  return { ...row, signed_url: data.signedUrl, signed_url_expires_at: expires };
};

export const markPendingMediaUploaded = async (
  scope: PendingMediaScope,
  rowId: string,
): Promise<void> => {
  const { data: auth } = await supabase.auth.getUser();
  const userId = auth?.user?.id ?? null;
  const { error } = await (supabase as any)
    .from(tableFor(scope))
    .update({
      uploaded_at: new Date().toISOString(),
      uploaded_by_user_id: userId,
    })
    .eq("id", rowId);
  if (error) throw error;
};

export const clearPendingMediaUploaded = async (
  scope: PendingMediaScope,
  rowId: string,
): Promise<void> => {
  const { error } = await (supabase as any)
    .from(tableFor(scope))
    .update({ uploaded_at: null, uploaded_by_user_id: null })
    .eq("id", rowId);
  if (error) throw error;
};

// ---------------------------------------------------------------------------
// Manual-chip seeding (Stage 7)
// ---------------------------------------------------------------------------
// Called from Publisher when the user toggles a manual chip "sent". Scans
// the publication body for Excerptus-hosted <img> tags and inserts one
// pending-media row per unique storage path for that destination. Rows are
// seeded with `signed_url: null` — the checklist UI mints URLs on demand
// via `refreshSignedUrl`.

import {
  extractExcerptusImagesFromHtml,
  type ManualMediaHit,
} from "@/lib/manualMediaScan";

export type SeedManualPendingMediaInput = {
  scope: PendingMediaScope;
  workspaceId: string;
  parentId: string;
  destinationId: string;
  body: string | null | undefined;
  /** Additional images to seed alongside any inline `<img>` hits in `body`.
   *  Used by the MicroPost path to seed the attached image from
   *  `content_micropost_images.image_storage_path`. */
  extraImages?: ManualMediaHit[];
};

/** Scan `body` and insert pending-media rows for any Excerptus images that
 *  aren't already tracked for this `(parentId, destinationId)` pair. Returns
 *  the number of new rows inserted. Safe to call repeatedly — idempotent on
 *  storage_path. */
export const seedManualPendingMedia = async (
  input: SeedManualPendingMediaInput,
): Promise<number> => {
  const inlineHits = extractExcerptusImagesFromHtml(input.body);
  const extra = input.extraImages ?? [];
  // Combine and dedupe on storage path so an extra image that also appears
  // inline doesn't get seeded twice.
  const combined: ManualMediaHit[] = [];
  const seen = new Set<string>();
  for (const h of [...inlineHits, ...extra]) {
    if (seen.has(h.path)) continue;
    seen.add(h.path);
    combined.push(h);
  }
  if (combined.length === 0) return 0;

  const table = tableFor(input.scope);
  const parentCol = parentColumnFor(input.scope);

  // Find what's already tracked for this destination so we don't double-seed.
  const { data: existing, error: selErr } = await (supabase as any)
    .from(table)
    .select("storage_path")
    .eq("workspace_id", input.workspaceId)
    .eq(parentCol, input.parentId)
    .eq("destination_id", input.destinationId);
  if (selErr) throw selErr;
  const known = new Set<string>(
    ((existing ?? []) as Array<{ storage_path: string }>).map((r) => r.storage_path),
  );

  const fresh = combined.filter((h) => !known.has(h.path));
  if (fresh.length === 0) return 0;

  const rows = fresh.map((h) => ({
    workspace_id: input.workspaceId,
    [parentCol]: input.parentId,
    dispatch_id: null,
    destination_id: input.destinationId,
    bucket: h.bucket,
    storage_path: h.path,
    filename: h.filename,
    alt_text: h.alt,
    original_url: h.original_url,
    signed_url: null,
    signed_url_expires_at: null,
  }));
  const { error: insErr } = await (supabase as any).from(table).insert(rows);
  if (insErr) throw insErr;
  return rows.length;
};

/** Counterpart to seedManualPendingMedia: drop the unresolved manual-chip rows
 *  for a `(parentId, destinationId)` pair when the user unmarks the chip.
 *  Preserves rows the user already checked off (`uploaded_at IS NOT NULL`) and
 *  rows seeded by a real dispatch (`dispatch_id IS NOT NULL`). Returns the
 *  number of rows deleted. */
export const clearManualPendingMedia = async (input: {
  scope: PendingMediaScope;
  workspaceId: string;
  parentId: string;
  destinationId: string;
}): Promise<number> => {
  const table = tableFor(input.scope);
  const parentCol = parentColumnFor(input.scope);
  const { data, error } = await (supabase as any)
    .from(table)
    .delete()
    .eq("workspace_id", input.workspaceId)
    .eq(parentCol, input.parentId)
    .eq("destination_id", input.destinationId)
    .is("uploaded_at", null)
    .is("dispatch_id", null)
    .select("id");
  if (error) throw error;
  return (data ?? []).length;
};


// ---------------------------------------------------------------------------
// Counts — pending vs total per (parent, destination) pair (D)
// ---------------------------------------------------------------------------

export type PendingMediaCount = { pending: number; total: number };

/** Map<parentId, Map<destinationId, { pending, total }>> for badge rendering
 *  on the Publisher chip row. One round-trip across all parent ids. */
export const listPendingMediaCounts = async (
  scope: PendingMediaScope,
  workspaceId: string,
  parentIds: string[],
): Promise<Map<string, Map<string, PendingMediaCount>>> => {
  const out = new Map<string, Map<string, PendingMediaCount>>();
  if (parentIds.length === 0) return out;
  const parentCol = parentColumnFor(scope);
  const { data, error } = await (supabase as any)
    .from(tableFor(scope))
    .select(`${parentCol}, destination_id, uploaded_at`)
    .eq("workspace_id", workspaceId)
    .in(parentCol, parentIds);
  if (error) throw error;
  for (const row of (data ?? []) as Array<Record<string, unknown>>) {
    const parentId = String(row[parentCol] ?? "");
    const destId = row.destination_id ? String(row.destination_id) : "";
    if (!parentId || !destId) continue;
    let perParent = out.get(parentId);
    if (!perParent) {
      perParent = new Map();
      out.set(parentId, perParent);
    }
    const cur = perParent.get(destId) ?? { pending: 0, total: 0 };
    cur.total += 1;
    if (!row.uploaded_at) cur.pending += 1;
    perParent.set(destId, cur);
  }
  return out;
};
